import { withAuth } from '@utilities/queries';
import {
  getGridView,
  updateGrid,
  createGrid,
  deleteGridView,
  getUserViews,
} from '@simplywallst/services';
import type { Grid } from '@simplywallst/services/lib/endpoints/getGridView';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import produce from 'immer';

interface GridPayload {
  rules: string;
  name: string;
  description?: string;
}

interface MutatePayload extends GridPayload {
  id: string;
}

type Options = {
  id?: string;
  onCreate?: (payload: { id: string; slug: string }) => void;
  onDelete?: () => void;
};

export type Response = {
  rules?: string;
  name: string;
  description: string;
  onlyMarketFiltered: boolean | null;
  crawlable: boolean;
};

type UserGrid = {
  id: string;
  name: string;
  rules: string;
  slug: string;
};

type UserGridList = UserGrid[];

const USER_GRID_LIST_QUERY = 'USER_GRID_LIST';
export const getQueryKey = (id: string) => `GRID_RULE_${id}`;
export const getDefault = (): Response => ({
  name: '',
  description: '',
  onlyMarketFiltered: true,
  crawlable: false,
});

const mutateQueryFn = async ({
  id,
  rules: nextRules,
  name: nextName,
  description: nextDescription,
}: MutatePayload) => {
  const response = await withAuth(updateGrid, id, {
    rule_json: JSON.parse(nextRules),
    name: nextName,
    description: nextDescription || '',
    // Hardcoding this for now as I'm not convinced it should be necessary to supply.
    // Snowflake scores also appear in the rules, soooo...
    value_score: 0,
    dividends_score: 0,
    future_performance_score: 0,
    health_score: 0,
    past_performance_score: 0,
  });
  const { name, rule_json, description } = response.data.data;
  return {
    rules: rule_json,
    name,
    description,
  };
};

const createQueryFn = async ({
  rules: newRules,
  name: newName,
  description: newDescription,
}: GridPayload) => {
  const response = await withAuth(createGrid, {
    rule_json: JSON.parse(newRules),
    name: newName,
    description: newDescription || '',
    // Hardcoding this for now as I'm not convinced it should be necessary to supply.
    // Snowflake scores also appear in the rules, soooo...
    value_score: 0,
    dividends_score: 0,
    future_performance_score: 0,
    health_score: 0,
    past_performance_score: 0,
  });
  const { id, name, rule_json, description, slug } = response.data.data;
  return {
    id: id.toString(),
    rules: rule_json,
    name,
    slug,
    description,
  };
};

const deleteQueryFn = async (id: string) => {
  const response = await withAuth(deleteGridView, id);
  return response.data;
};

export const makeQueryFn = (id: string) => async (): Promise<Response> => {
  const response = await getGridView(id);
  const { name, rule_json, description, only_market_filtered, status } =
    response.data.data;
  return {
    rules: rule_json,
    name,
    description,
    onlyMarketFiltered: only_market_filtered,
    crawlable: !Boolean(status),
  };
};

/**
 * ### Description
 *
 * This hook manages grid views (screener filters). With it you can:
 *
 * - Retrieve existing grid views by supplying an `id`.
 * - Create a new grid view if the current user is authenticated to do so.
 * - Mutate an existing grid view if the current user is authenticated to do so.
 * - Delete an existing grid view if the current user is authenticated to do so.
 *
 * ### Options
 *
 * - `id` of the grid that you want returned
 * - `onCreate` is a callback function that fires when a new grid has been successfully created
 * - `onDelete` is a callback function that fires when an existing grid has been successfully deleted
 */
export const useGridRules = ({ id = '0', onCreate, onDelete }: Options) => {
  const queryClient = useQueryClient();
  const queryKey = getQueryKey(id);
  const { data = getDefault(), status } = useQuery(queryKey, makeQueryFn(id), {
    retry: false,
    enabled: id !== '0' && id !== 'current', // `current` is a legacy redux id that can be deprecated in due course
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { mutate: mutateGrid, status: mutationStatus } = useMutation(
    async (grid: GridPayload) =>
      mutateQueryFn({
        id,
        ...grid,
      }),
    {
      onMutate: async ({
        rules,
        name,
        description,
      }): Promise<{ prevGrid?: Response }> => {
        const prevGrid = queryClient.getQueryData<Response>(queryKey);
        queryClient.setQueryData<Response>(queryKey, (state = getDefault()) =>
          produce(state, (draft) => {
            draft.name = name;
            draft.rules = rules;
            draft.description = description || '';
          })
        );
        return {
          prevGrid,
        };
      },
      onError: (err, payload, context) => {
        const prevGrid = (context as { prevGrid?: Response }).prevGrid;
        if (typeof prevGrid !== 'undefined') {
          queryClient.setQueryData<Response>(queryKey, prevGrid);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(queryKey);
        queryClient.invalidateQueries(USER_GRID_LIST_QUERY);
      },
    }
  );

  const { mutate: createGrid, status: creationStatus } = useMutation(
    createQueryFn,
    {
      onSuccess: ({ id, name, rules, description, slug }) => {
        queryClient.setQueryData<Response>(
          getQueryKey(id),
          (state = getDefault()) =>
            produce(state, (draft) => {
              draft.name = name;
              draft.rules = rules;
              draft.description = description;
            })
        );
        queryClient.setQueryData<UserGridList>(
          USER_GRID_LIST_QUERY,
          (state = []) =>
            produce(state, (draft) => {
              if (draft.findIndex((n) => n.id === id) !== -1) return;
              draft.push({
                id,
                name,
                rules,
                slug,
              });
            })
        );
        if (typeof onCreate !== 'undefined') {
          onCreate({ id, slug });
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(USER_GRID_LIST_QUERY);
      },
    }
  );

  const { mutate: deleteGrid, status: deletionStatus } = useMutation(
    deleteQueryFn,
    {
      onSuccess: (data, id) => {
        queryClient.setQueryData<UserGridList>(
          USER_GRID_LIST_QUERY,
          (state = []) =>
            produce(state, (draft) => {
              const index = draft.findIndex((n) => n.id === id);
              if (index !== -1) draft.splice(index, 1);
            })
        );
        queryClient.removeQueries(getQueryKey(id));
        if (typeof onDelete !== 'undefined') onDelete();
      },
      onSettled: () => {
        queryClient.invalidateQueries(USER_GRID_LIST_QUERY);
      },
    }
  );

  return {
    data,
    status,
    mutateGrid,
    mutationStatus,
    createGrid,
    creationStatus,
    deleteGrid,
    deletionStatus,
  };
};

const listMapper = (n: Grid) => ({
  id: n.id.toString(),
  name: n.name,
  rules: n.rule_json,
  slug: n.slug,
});

export const useUserGridList = (isGuestUser = true) => {
  const { data = [], status } = useQuery(
    USER_GRID_LIST_QUERY,
    async (): Promise<UserGridList> => {
      const response = await withAuth(getUserViews);
      return response.data.data.map(listMapper);
    },
    {
      enabled: isGuestUser === false,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  return {
    data,
    status,
  };
};
